import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppErrorBoundary } from "./components/layout/AppErrorBoundary";
import { PublicShell } from "./components/layout/PublicShell";
import { PortalLayout } from "./components/layout/PortalLayout";
import { RequireAuth } from "./components/layout/RequireAuth";
import { AdminClientsPage } from "./pages/admin/AdminClientsPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminMemoryMediaVaultPage } from "./pages/admin/AdminMemoryMediaVaultPage";
import {
  AdminActivityLogPage,
  AdminClientDetailPage,
  AdminEventsPage,
  AdminMediaPage,
  AdminSettingsPage,
  AdminStorageSetupPage,
} from "./pages/admin/AdminUtilityPages";
import { DashboardPage } from "./pages/client/DashboardPage";
import { InstructionsPage } from "./pages/client/InstructionsPage";
import { MediaManagementPage } from "./pages/client/MediaManagementPage";
import { MemoryMediaVaultPage } from "./pages/client/MemoryMediaVaultPage";
import { SettingsPage } from "./pages/client/SettingsPage";
import { StorageBillingPage } from "./pages/client/StorageBillingPage";
import { UploadLinkPage } from "./pages/client/UploadLinkPage";
import { CollabRecoverClientPortalPage } from "./pages/client/CollabRecoverClientPortalPage";
import { AdminFormsDirectoryPage } from "./pages/forms/AdminFormsDirectoryPage";
import { BusinessCenterOSHomePage } from "./pages/forms/BusinessCenterOSHomePage";
import { AgreementRecordsPage } from "./pages/forms/AgreementRecordsPage";
import { ExecutiveDashboardPage } from "./pages/forms/ExecutiveDashboardPage";
import { ClientJourneyDashboardPage } from "./pages/forms/ClientJourneyDashboardPage";
import { ProposalWorkspacePage } from "./pages/forms/ProposalWorkspacePage";
import { TruevanceProposalCenterPage } from "./pages/forms/TruevanceProposalCenterPage";
import { TruevanceBusinessCenterPage } from "./pages/forms/TruevanceBusinessCenterPages";
import { TruevanceConnectPage } from "./pages/forms/TruevanceConnectPages";
import { TruevanceSalesCenterPage } from "./pages/forms/TruevanceSalesCenterPage";
import { BusinessCenterOSClientCenterPage } from "./pages/forms/BusinessCenterOSClientCenterPage";
import { BusinessCenterOSCompanyUsersPage } from "./pages/forms/BusinessCenterOSCompanyUsersPage";
import { BusinessCenterInvitationAcceptancePage } from "./pages/forms/BusinessCenterInvitationAcceptancePage";
import { BusinessCenterOSDocumentCenterPage } from "./pages/forms/BusinessCenterOSDocumentCenterPage";
import { BusinessCenterOSOnboardingCenterPage } from "./pages/forms/BusinessCenterOSOnboardingCenterPage";
import { BusinessCenterOSRolesCenterPage } from "./pages/forms/BusinessCenterOSRolesCenterPage";
import { InternalUserWorkspacePage } from "./pages/forms/InternalUserWorkspacePage";
import { BusinessCenterOSProjectCenterPage } from "./pages/forms/BusinessCenterOSProjectCenterPage";
import { AutomationEnginePage } from "./pages/forms/AutomationEnginePage";
import { BusinessCenterOSTasksPage } from "./pages/forms/BusinessCenterOSTasksPage";
import { OperationalRegistryInspectorPage } from "./pages/forms/OperationalRegistryInspectorPage";
import { UniversalBusinessNotesCenterPage } from "./pages/forms/UniversalBusinessNotesCenterPage";
import { WorkspaceProvisioningCenterPage } from "./pages/forms/WorkspaceProvisioningCenterPage";
import { WhiteLabelBrandingCenterPage } from "./pages/forms/WhiteLabelBrandingCenterPage";
import { ARRecoveryWorkspacePage } from "./pages/forms/ARRecoveryPages";
import { TruevanceAnalyticsCenterPage } from "./pages/forms/TruevanceAnalyticsCenterPage";
import { TruevanceCommunicationCenterPage } from "./pages/forms/TruevanceCommunicationCenterPage";
import {
  TruevanceAssessmentDetailPage,
  TruevanceAssessmentManagementPage,
  ConsultingAssessmentDetailPage,
  ConsultingAssessmentEnginePage,
  ConsultingRecommendationDetailPage,
  ConsultingRecommendationEnginePage,
  ImplementationRoadmapDetailPage,
  ImplementationRoadmapEnginePage,
  TruevanceOpportunityDetailPage,
  TruevanceOpportunityManagementPage,
  TruevanceProjectDetailPage,
  TruevanceProjectManagementPage,
} from "./pages/forms/TruevanceOperationsPages";
import { FormsClientDirectoryPage, FormsClientProfilePage } from "./pages/forms/ClientManagementPages";
import { FormsAdminLoginPage } from "./pages/forms/FormsAdminLoginPage";
import { AdminWorkflowPage } from "./pages/forms/AdminWorkflowPages";
import { EventInquiryFormPage } from "./pages/forms/EventInquiryFormPage";
import { AdminLeadDetailPage, AdminLeadsPage } from "./pages/forms/LeadManagementPages";
import {
  AdminClientDirectoryPage as AdminClientDirectoryFormPage,
  AdminClientFeedbackPage,
  AdminDiscoveryCallNotesPage,
  AdminGeneralSupportRequestPage,
  AdminKickoffCallPrepPage,
  AdminPartnerReferralTrackerPage,
  AdminProjectDashboardPage as AdminProjectDashboardFormPage,
} from "./pages/forms/AdminServiceFormPages";
import { PaymentRecordsPage } from "./pages/forms/PaymentRecordsPage";
import { StripePaymentSettingsPage } from "./pages/forms/StripePaymentSettingsPage";
import { ArchiveHubFormPage } from "./pages/forms/ArchiveHubFormPage";
import { MemoryHubFormPage } from "./pages/forms/MemoryHubFormPage";
import { MemoryMediaVaultFormPage } from "./pages/forms/MemoryMediaVaultFormPage";
import { PartnerReferralFormPage } from "./pages/forms/PartnerReferralFormPage";
import {
  AgreementSignaturePage,
  OnboardingCenterPage,
  PaymentPage,
  ProposalAcceptancePage,
} from "./pages/forms/JourneyClientPages";
import { RSVPHubFormPage } from "./pages/forms/RSVPHubFormPage";
import { SignatureHighlightReelFormPage } from "./pages/forms/SignatureHighlightReelFormPage";
import { SignatureSongFormPage } from "./pages/forms/SignatureSongFormPage";
import { GalleryPage } from "./pages/GalleryPage";
import { LoginPage } from "./pages/LoginPage";
import { MemoryMediaVaultDemoPage } from "./pages/MemoryMediaVaultDemoPage";
import { MemoryHubPage } from "./pages/MemoryHubPage";
import { UploadPage } from "./pages/UploadPage";
import { getSession } from "./lib/api";
import { resolveAuthenticatedLandingRoute } from "./lib/defaultLandingWorkspace";

export function App() {
  return (
    <AppErrorBoundary>
      <Routes>
        <Route path="/" element={<FormsPortalRoot />} />
        <Route path="/event-inquiry" element={<EventInquiryFormPage />} />
        <Route path="/rsvp-hub" element={<RSVPHubFormPage />} />
        <Route path="/memory-hub" element={<MemoryHubFormPage />} />
        <Route path="/archive-hub" element={<ArchiveHubFormPage />} />
        <Route path="/memory-media-vault" element={<MemoryMediaVaultFormPage />} />
        <Route path="/signature-song" element={<SignatureSongFormPage />} />
        <Route path="/signature-highlight-reel" element={<SignatureHighlightReelFormPage />} />
        <Route path="/partner-referral" element={<PartnerReferralFormPage />} />
        <Route path="/proposal/:token" element={<ProposalAcceptancePage />} />
        <Route path="/agreement/:token" element={<AgreementSignaturePage />} />
        <Route path="/payment/:token" element={<PaymentPage />} />
        <Route path="/onboarding/:token" element={<OnboardingCenterPage />} />

        <Route element={<PublicShell />}>
          <Route path="/upload-link/:uploadSlug" element={<UploadPage />} />
          <Route path="/upload/:uploadSlug" element={<UploadPage />} />
          <Route path="/upload/:clientSlug/:eventSlug" element={<UploadPage />} />
          <Route path="/gallery/:clientSlug/:eventSlug" element={<GalleryPage />} />
          <Route path="/memory-hub/:clientSlug/:eventSlug" element={<MemoryHubPage />} />
        </Route>

        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/login" element={<FormsAdminLoginPage />} />
        <Route path="/admin/accept-invitation/:token" element={<BusinessCenterInvitationAcceptancePage />} />
        <Route path="/demo/memory-media-vault" element={<MemoryMediaVaultDemoPage page="hub" />} />
        <Route path="/demo/memory-media-vault/upload" element={<MemoryMediaVaultDemoPage page="upload" />} />
        <Route path="/demo/memory-media-vault/gallery" element={<MemoryMediaVaultDemoPage page="gallery" />} />

        <Route element={<RequireAuth />}>
          <Route path="/client/portal/*" element={<CollabRecoverClientPortalPage />} />
          <Route element={<PortalLayout />}>
            <Route path="/client/dashboard" element={<DashboardPage />} />
            <Route path="/client/memory-hub/vault" element={<MemoryMediaVaultPage />} />
            <Route path="/client/pending" element={<MediaManagementPage view="pending" />} />
            <Route path="/client/approved" element={<MediaManagementPage view="approved" />} />
            <Route path="/client/featured" element={<MediaManagementPage view="featured" />} />
            <Route path="/client/hidden" element={<MediaManagementPage view="hidden" />} />
            <Route path="/client/not-approved" element={<MediaManagementPage view="not-approved" />} />
            <Route path="/client/upload-link" element={<UploadLinkPage />} />
            <Route path="/client/storage-billing" element={<StorageBillingPage />} />
            <Route path="/client/settings" element={<SettingsPage />} />
            <Route path="/client/instructions" element={<InstructionsPage />} />
          </Route>
        </Route>

        <Route element={<RequireAuth admin />}>
        <Route path="/admin/forms-directory" element={<AdminFormsDirectoryPage />} />
        <Route path="/admin/business-center-os" element={<BusinessCenterOSHomePage />} />
        <Route path="/admin/my-workspace" element={<InternalUserWorkspacePage />} />
        <Route path="/admin/my-profile" element={<InternalUserWorkspacePage profileOnly />} />
        <Route path="/admin/executive-dashboard" element={<ExecutiveDashboardPage />} />
          <Route path="/admin/journey-dashboard" element={<ClientJourneyDashboardPage />} />
          <Route path="/admin/proposal-dashboard" element={<ClientJourneyDashboardPage />} />
          <Route path="/admin/payment-settings" element={<StripePaymentSettingsPage />} />
          <Route path="/admin/truevance-business-center" element={<TruevanceBusinessCenterPage center="home" />} />
          <Route path="/admin/truevance-connect" element={<TruevanceBusinessCenterPage center="home" />} />
          <Route path="/admin/truevance-sales-center" element={<TruevanceSalesCenterPage />} />
          <Route path="/admin/truevance-assessment-center" element={<ConsultingAssessmentEnginePage />} />
          <Route path="/admin/truevance-proposal-center" element={<TruevanceProposalCenterPage />} />
          <Route path="/admin/truevance-proposal-center/:proposalId" element={<TruevanceProposalCenterPage />} />
          <Route path="/admin/truevance-client-center" element={<BusinessCenterOSClientCenterPage />} />
          <Route path="/admin/truevance-company-users" element={<BusinessCenterOSCompanyUsersPage />} />
          <Route path="/admin/truevance-project-center" element={<BusinessCenterOSProjectCenterPage />} />
          <Route path="/admin/truevance-project-center/:projectId" element={<BusinessCenterOSProjectCenterPage />} />
          <Route path="/admin/truevance-tasks" element={<BusinessCenterOSTasksPage />} />
          <Route path="/admin/business-notes" element={<UniversalBusinessNotesCenterPage />} />
          <Route path="/admin/operational-registry" element={<OperationalRegistryInspectorPage />} />
          <Route path="/admin/truevance-document-center" element={<BusinessCenterOSDocumentCenterPage />} />
          <Route path="/admin/universal-user-management" element={<BusinessCenterOSCompanyUsersPage />} />
          <Route path="/admin/client-onboarding-center" element={<BusinessCenterOSOnboardingCenterPage />} />
          <Route path="/admin/workspace-provisioning-center" element={<WorkspaceProvisioningCenterPage />} />
          <Route path="/admin/white-label-branding-center" element={<WhiteLabelBrandingCenterPage />} />
          <Route path="/admin/automation-engine" element={<AutomationEnginePage />} />
          <Route path="/admin/truevance-roles-center" element={<BusinessCenterOSRolesCenterPage />} />
          <Route path="/admin/truevance-communication-center" element={<TruevanceCommunicationCenterPage />} />
          <Route path="/admin/truevance-analytics-center" element={<TruevanceAnalyticsCenterPage />} />
          <Route path="/admin/truevance-settings-center" element={<TruevanceBusinessCenterPage center="settings-center" />} />
          <Route path="/admin/ar-recovery" element={<ARRecoveryWorkspacePage section="dashboard" />} />
          <Route path="/admin/ar-recovery/client-management" element={<ARRecoveryWorkspacePage section="client-management" />} />
          <Route path="/admin/ar-recovery/client-exchange" element={<ARRecoveryWorkspacePage section="client-exchange" />} />
          <Route path="/admin/ar-recovery/customer-account-management" element={<ARRecoveryWorkspacePage section="customer-account-management" />} />
          <Route path="/admin/ar-recovery/customer-accounts" element={<ARRecoveryWorkspacePage section="customer-accounts" />} />
          <Route path="/admin/ar-recovery/aging-management" element={<ARRecoveryWorkspacePage section="aging-management" />} />
          <Route path="/admin/ar-recovery/promise-to-pay" element={<ARRecoveryWorkspacePage section="promise-to-pay" />} />
          <Route path="/admin/ar-recovery/smart-tickler" element={<ARRecoveryWorkspacePage section="smart-tickler" />} />
          <Route path="/admin/ar-recovery/collection-letters" element={<ARRecoveryWorkspacePage section="collection-letters" />} />
          <Route path="/admin/ar-recovery/document-center" element={<ARRecoveryWorkspacePage section="document-center" />} />
          <Route path="/admin/ar-recovery/statements" element={<ARRecoveryWorkspacePage section="statements" />} />
          <Route path="/admin/ar-recovery/statements/:statementId" element={<ARRecoveryWorkspacePage section="statement-execution" />} />
          <Route path="/admin/ar-recovery/payments" element={<ARRecoveryWorkspacePage section="payment-center" />} />
          <Route path="/admin/ar-recovery/settlements" element={<ARRecoveryWorkspacePage section="settlement-center" />} />
          <Route path="/admin/ar-recovery/billing" element={<ARRecoveryWorkspacePage section="billing-center" />} />
          <Route path="/admin/ar-recovery/recovery-activity" element={<ARRecoveryWorkspacePage section="recovery-activity" />} />
          <Route path="/admin/ar-recovery/recovery-reporting" element={<ARRecoveryWorkspacePage section="recovery-reporting" />} />
          <Route path="/admin/ar-recovery/recovery-analytics" element={<ARRecoveryWorkspacePage section="recovery-analytics" />} />
          <Route path="/admin/ar-recovery/manager-command-center" element={<ARRecoveryWorkspacePage section="manager-command-center" />} />
          <Route path="/admin/ar-recovery/delivery-operations" element={<ARRecoveryWorkspacePage section="delivery-operations" />} />
          <Route path="/admin/ar-recovery/client-portal" element={<ARRecoveryWorkspacePage section="client-portal" />} />
          <Route path="/admin/ar-recovery/white-label-settings" element={<ARRecoveryWorkspacePage section="white-label-settings" />} />
          <Route path="/admin/ar-recovery/commercialization-center" element={<ARRecoveryWorkspacePage section="commercialization-center" />} />
          <Route path="/admin/ar-recovery/enterprise-readiness" element={<ARRecoveryWorkspacePage section="commercialization-center" />} />
          <Route path="/admin/consulting-pipeline" element={<TruevanceConnectPage workspace="consulting-pipeline" />} />
          <Route path="/admin/assessment-framework" element={<TruevanceConnectPage workspace="assessment-framework" />} />
          <Route path="/admin/client-engagement-dashboard" element={<TruevanceConnectPage workspace="client-engagement-dashboard" />} />
          <Route path="/admin/service-categories" element={<TruevanceConnectPage workspace="service-categories" />} />
          <Route path="/admin/platform-architecture" element={<TruevanceConnectPage workspace="platform-architecture" />} />
          <Route path="/admin/truevance-opportunities" element={<TruevanceOpportunityManagementPage />} />
          <Route path="/admin/truevance-opportunities/:opportunityId" element={<TruevanceOpportunityDetailPage />} />
          <Route path="/admin/truevance-assessments" element={<TruevanceAssessmentManagementPage />} />
          <Route path="/admin/truevance-assessments/:assessmentId" element={<TruevanceAssessmentDetailPage />} />
          <Route path="/admin/truevance-projects" element={<TruevanceProjectManagementPage />} />
          <Route path="/admin/truevance-projects/:projectId" element={<TruevanceProjectDetailPage />} />
          <Route path="/admin/consulting-assessments" element={<ConsultingAssessmentEnginePage />} />
          <Route path="/admin/consulting-assessments/:assessmentId" element={<ConsultingAssessmentDetailPage />} />
          <Route path="/admin/truevance-recommendations" element={<ConsultingRecommendationEnginePage />} />
          <Route path="/admin/truevance-recommendations/:recommendationId" element={<ConsultingRecommendationDetailPage />} />
          <Route path="/admin/implementation-roadmaps" element={<ImplementationRoadmapEnginePage />} />
          <Route path="/admin/implementation-roadmaps/:roadmapId" element={<ImplementationRoadmapDetailPage />} />
          <Route path="/admin/leads" element={<AdminLeadsPage />} />
          <Route path="/admin/leads/:leadId" element={<AdminLeadDetailPage />} />
          <Route path="/admin/forms-client-directory" element={<FormsClientDirectoryPage />} />
          <Route path="/admin/forms-client/:id" element={<FormsClientProfilePage />} />
          <Route path="/admin/proposals" element={<ProposalWorkspacePage />} />
          <Route path="/admin/agreements" element={<AgreementRecordsPage />} />
          <Route path="/admin/agreement-records" element={<AgreementRecordsPage />} />
          <Route path="/admin/agreement-records/:agreementId" element={<AgreementRecordsPage />} />
          <Route path="/admin/payments" element={<PaymentRecordsPage />} />
          <Route path="/admin/payment-records" element={<PaymentRecordsPage />} />
          <Route path="/admin/payment-records/:paymentId" element={<PaymentRecordsPage />} />
          <Route path="/admin/service-agreements" element={<PaymentRecordsPage />} />
          <Route path="/admin/scheduler" element={<AdminWorkflowPage workflow="scheduler-settings" />} />
          <Route path="/admin/scheduler-settings" element={<AdminWorkflowPage workflow="scheduler-settings" />} />
          <Route path="/admin/scheduling" element={<AdminWorkflowPage workflow="scheduler-settings" />} />
          <Route path="/admin/welcome-email-settings" element={<AdminWorkflowPage workflow="welcome-email-settings" />} />
          <Route path="/admin/onboarding-settings" element={<AdminWorkflowPage workflow="onboarding-settings" />} />
          <Route path="/admin/discovery-call-notes" element={<AdminDiscoveryCallNotesPage />} />
          <Route path="/admin/kickoff-call-prep" element={<AdminKickoffCallPrepPage />} />
          <Route path="/admin/client-directory" element={<AdminClientDirectoryFormPage />} />
          <Route path="/admin/project-dashboard" element={<AdminProjectDashboardFormPage />} />
          <Route path="/admin/partner-referral-tracker" element={<AdminPartnerReferralTrackerPage />} />
          <Route path="/admin/client-feedback" element={<AdminClientFeedbackPage />} />
          <Route path="/admin/general-support-request" element={<AdminGeneralSupportRequestPage />} />
          <Route element={<PortalLayout mode="admin" />}>
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/clients" element={<AdminClientsPage />} />
            <Route path="/admin/memory-hub/vault" element={<AdminMemoryMediaVaultPage />} />
            <Route path="/admin/client/:clientId" element={<AdminClientDetailPage />} />
            <Route path="/admin/events" element={<AdminEventsPage />} />
            <Route path="/admin/media" element={<AdminMediaPage />} />
            <Route path="/admin/storage-setup" element={<AdminStorageSetupPage />} />
            <Route path="/admin/activity-log" element={<AdminActivityLogPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<SharedPortalEntry />} />
      </Routes>
    </AppErrorBoundary>
  );
}

function FormsPortalRoot() {
  if (typeof window !== "undefined" && window.location.hostname === "start.kreatethedate.com") {
    return <EventInquiryFormPage />;
  }

  return <SharedPortalEntry />;
}

function SharedPortalEntry() {
  const [destination, setDestination] = useState("");

  useEffect(() => {
    let active = true;

    getSession()
      .then(async (session) => {
        if (!active) return;
        if (!session.user) {
          setDestination("/admin/login");
          return;
        }
        const landingRoute = await resolveAuthenticatedLandingRoute({
          email: session.user.email,
          role: session.user.role,
        });
        if (active) setDestination(landingRoute || "/admin/login");
      })
      .catch(() => {
        if (active) setDestination("/admin/login");
      });

    return () => {
      active = false;
    };
  }, []);

  if (destination) {
    return <Navigate to={destination} replace />;
  }

  return (
    <main className="grid min-h-screen place-items-center bg-[#F8FAFC] px-6 py-10 text-[#0A2540]">
      <section className="w-full max-w-md rounded-3xl border border-[#D8E7F3] bg-white p-8 text-center shadow-[0_24px_80px_rgba(10,37,64,0.10)]">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#14B8A6]">Universal Circuit Hub</p>
        <h1 className="mt-4 text-3xl font-black tracking-[-0.04em]">Checking Secure Access</h1>
        <p className="mt-3 text-sm font-semibold leading-6 text-[#475569]">
          Routing you to the correct protected workspace.
        </p>
      </section>
    </main>
  );
}
